import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, CreditCard, Receipt } from "lucide-react";
import {
  ActivityAvailabilityMode,
  ActivityCategory,
  ActivityPaymentModel,
  BookingQuantityMode,
} from "@/types";
import type { ActivityCreate, AddressValidationState } from "@/types";
import { useInvalidAddressSaveConfirm } from "@/hooks/useInvalidAddressSaveConfirm";
import {
  ACTIVITY_TYPE_CONFIG,
  ACTIVITY_TYPE_ORDER,
  activityTypeLabel,
} from "@/constants";
import { resolveLocale, type SupportedLocale } from "@/lib/locales";
import {
  ACTIVITY_DESCRIPTION_MAX_LENGTH,
  ACTIVITY_NAME_MAX_LENGTH,
  buildItemDescriptionTranslations,
  buildRequiredTranslations,
  findAutoTranslateSourceLanguage,
  findMissingTranslations,
  findTranslationOverMaxLength,
  hasPartialTranslations,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import {
  applyAutoTranslateResult,
  useCatalogAutoTranslate,
} from "@/hooks/useCatalogAutoTranslate";
import {
  CatalogTranslatedField,
  CatalogTranslatePanel,
  EditableGeocodedLocation,
  PickerPanel,
  QueryLoadError,
  RegionSelect,
} from "@/components/common";
import {
  emptyGeocodedLocation,
  type GeocodedLocationValue,
} from "@/lib/geocodedLocation";
import {
  Button,
  FieldError,
  Input,
  Label,
  Select,
  Modal,
  ModalFooter,
  ConfirmModal,
  PercentInput,
  fieldErrorId,
} from "@/components/ui";
import { sellerAccountService } from "@/services/sellerAccountService";
import { hostService } from "@/services/hostService";
import { salesAgentService } from "@/services/salesAgentService";
import { cn } from "@/lib/utils";
import { matchesTextQuery } from "@/lib/textSearch";
import { queryLoadFailure } from "@/lib/queryLoadFailure";
import { rateFromPercentInput, rateFromUIPercent } from "@/lib/commissionRate";

function buildActivityInputSchema(t: TFunction<"activities">) {
  return z
    .object({
      category: z.enum(ActivityCategory).optional(),
      payment_model: z.enum(ActivityPaymentModel).optional(),
      commission_percent: z
        .number()
        .min(0, t("zod.commissionPercentRange"))
        .max(100, t("zod.commissionPercentRange")),
    })
    .superRefine((data, ctx) => {
      if (
        data.commission_percent === undefined ||
        Number.isNaN(data.commission_percent)
      ) {
        ctx.addIssue({
          code: "custom",
          message: t("zod.commissionPercentRequired"),
          path: ["commission_percent"],
        });
      }
      if (data.category === undefined) {
        ctx.addIssue({
          code: "custom",
          message: t("zod.categoryRequired"),
          path: ["category"],
        });
      }
      if (data.payment_model === undefined) {
        ctx.addIssue({
          code: "custom",
          message: t("zod.paymentModelRequired"),
          path: ["payment_model"],
        });
      }
    });
}

type ActivityFormData = z.infer<ReturnType<typeof buildActivityInputSchema>>;

interface CreateActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ActivityCreate) => void;
  isLoading?: boolean;
  /** When set (e.g. from partner/host detail), owner selector is hidden and this user id is used. */
  fixedOwnerUserId?: string;
}

type OwnerOption = { value: string; label: string };

type AdminWizardStep = 1 | 2 | 3;

const DEFAULT_FORM: ActivityFormData = {
  category: undefined,
  payment_model: undefined,
  commission_percent: Number.NaN,
};

function WizardStepper({
  current,
  steps,
  counterLabel,
}: {
  current: number;
  steps: string[];
  counterLabel: string;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium tabular-nums text-surface-500">
        {counterLabel}
      </p>
      <ol className="grid grid-cols-3 gap-2">
        {steps.map((label, index) => {
          const step = index + 1;
          const done = step < current;
          const active = step === current;
          return (
            <li
              key={label}
              className="min-w-0"
              aria-current={active ? "step" : undefined}
            >
              <span
                className={cn(
                  "block h-1 rounded-full transition-colors",
                  done || active ? "bg-primary-500" : "bg-surface-200",
                )}
                aria-hidden
              />
              <span
                className={cn(
                  "mt-1.5 flex items-center gap-1 truncate text-[13px]",
                  active
                    ? "font-medium text-surface-900"
                    : done
                      ? "text-surface-600"
                      : "text-surface-400",
                )}
              >
                {done ? (
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary-600" aria-hidden />
                ) : null}
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function CreateActivityModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  fixedOwnerUserId,
}: CreateActivityModalProps) {
  const { t, i18n } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const { t: tValidation } = useTranslation("validation");
  const { t: tErrors } = useTranslation("errors");
  const [nameTranslations, setNameTranslations] = useState<TranslationFormValues>({});
  const [nameError, setNameError] = useState<string | undefined>();
  const [descriptionTranslations, setDescriptionTranslations] = useState<TranslationFormValues>({});
  const [descriptionError, setDescriptionError] = useState<string | undefined>();
  const [copyLanguage, setCopyLanguage] = useState<SupportedLocale>(() =>
    resolveLocale(i18n.language),
  );
  const [ownerSearch, setOwnerSearch] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [regionId, setRegionId] = useState("");
  const [regionError, setRegionError] = useState<string | undefined>();
  const [salesAgentUserId, setSalesAgentUserId] = useState("");
  const [salesAgentSearch, setSalesAgentSearch] = useState("");
  const [salesAgentCommissionPct, setSalesAgentCommissionPct] = useState("");
  const [salesAgentCommissionError, setSalesAgentCommissionError] = useState<
    string | undefined
  >();
  const [adminStep, setAdminStep] = useState<AdminWizardStep>(1);
  const [noFixedLocation, setNoFixedLocation] = useState(false);
  const [location, setLocation] = useState<GeocodedLocationValue>(
    emptyGeocodedLocation,
  );
  const [addressValidation, setAddressValidation] =
    useState<AddressValidationState>({ status: "idle" });
  const locationDraftBackup = useRef<GeocodedLocationValue>(
    emptyGeocodedLocation(),
  );

  const activityInputSchema = useMemo(() => buildActivityInputSchema(t), [t]);
  const resolver = useMemo(
    () => zodResolver(activityInputSchema),
    [activityInputSchema],
  );

  const paymentModelCards = useMemo(
    () =>
      [
        {
          value: ActivityPaymentModel.UPFRONT,
          label: t("createModal.paymentModelUpfront"),
          description: t("createModal.paymentModelUpfrontDesc"),
          Icon: CreditCard,
        },
        {
          value: ActivityPaymentModel.RECEIPT_BASED,
          label: t("createModal.paymentModelReceiptBased"),
          description: t("createModal.paymentModelReceiptBasedDesc"),
          Icon: Receipt,
        },
      ] as const,
    [t],
  );

  const partnersQuery = useQuery({
    queryKey: ["partners", "activity-create"],
    queryFn: () => sellerAccountService.getAll(),
    enabled: isOpen && !fixedOwnerUserId,
  });
  const { data: partners = [] } = partnersQuery;

  const hostsQuery = useQuery({
    queryKey: ["hosts", "activity-create"],
    queryFn: () => hostService.getAll(),
    enabled: isOpen && !fixedOwnerUserId,
  });
  const { data: hosts = [] } = hostsQuery;

  const salesAgentsQuery = useQuery({
    queryKey: ["sales-agents", "activity-create"],
    queryFn: () => salesAgentService.listForAdmin(),
    enabled: isOpen,
  });
  const { data: salesAgents = [] } = salesAgentsQuery;

  const ownerOptions: OwnerOption[] = useMemo(() => {
    const p = partners.map((x) => ({
      value: x.user_id,
      label: `${x.name} ${t("createModal.partnerSuffix")}`,
    }));
    const h = hosts.map((x) => ({
      value: x.user_id,
      label: `${x.name} ${t("createModal.hostSuffix")}`,
    }));
    return [...p, ...h].sort((a, b) => a.label.localeCompare(b.label));
  }, [partners, hosts, t]);

  const ownerSelectOptions = useMemo(() => {
    let list = ownerSearch.trim()
      ? ownerOptions.filter((o) =>
          matchesTextQuery(ownerSearch, [o.label]),
        )
      : ownerOptions;
    if (ownerUserId && !list.some((o) => o.value === ownerUserId)) {
      const sel = ownerOptions.find((o) => o.value === ownerUserId);
      if (sel) {
        list = [{ value: sel.value, label: sel.label }, ...list];
      }
    }
    return list.map((o) => ({ value: o.value, label: o.label }));
  }, [ownerOptions, ownerSearch, ownerUserId]);

  const ownersLoading = partnersQuery.isLoading || hostsQuery.isLoading;
  /** Only claim "no owners" once both owner lists have actually loaded. */
  const ownersLoaded = partnersQuery.isSuccess && hostsQuery.isSuccess;
  const ownersLoadError = queryLoadFailure(
    {
      error: partnersQuery.error ?? hostsQuery.error,
      refetch: () =>
        Promise.all([partnersQuery.refetch(), hostsQuery.refetch()]),
    },
    tErrors("loadOwners"),
  );

  const salesAgentsDisplayed = useMemo(() => {
    return salesAgentSearch.trim()
      ? salesAgents.filter((sa) => matchesTextQuery(salesAgentSearch, [sa.email]))
      : salesAgents;
  }, [salesAgents, salesAgentSearch]);

  useEffect(() => {
    if (isOpen) {
      setNameTranslations({});
      setNameError(undefined);
      setDescriptionTranslations({});
      setDescriptionError(undefined);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setAdminStep(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (adminStep !== 3) {
      setNoFixedLocation(false);
      locationDraftBackup.current = emptyGeocodedLocation();
      setLocation(emptyGeocodedLocation());
    }
  }, [adminStep]);

  // Reset admin-only fields when the modal opens or fixed owner changes.
  // Do not depend on `ownerOptions` here — that reference updates when queries
  // resolve/refetch and would wipe the sales-agent search on every update.
  useEffect(() => {
    if (!isOpen) return;
    setSalesAgentUserId("");
    setSalesAgentSearch("");
    if (fixedOwnerUserId) {
      setOwnerUserId(fixedOwnerUserId);
      setOwnerSearch("");
      return;
    }
    setOwnerSearch("");
  }, [isOpen, fixedOwnerUserId]);

  // Default owner from loaded partner/host list (does not touch sales agent search).
  useEffect(() => {
    if (!isOpen || fixedOwnerUserId) return;
    setOwnerUserId((prev) => {
      if (prev && ownerOptions.some((o) => o.value === prev)) return prev;
      return ownerOptions[0]?.value ?? "";
    });
  }, [isOpen, fixedOwnerUserId, ownerOptions]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<ActivityFormData>({
    resolver,
    defaultValues: DEFAULT_FORM,
  });

  const categoryValue = watch("category");
  const paymentModelValue = watch("payment_model");

  const formId = "create-activity-form";
  const paymentModelGroupId = "create-activity-payment-model";
  const paymentModelErrorId = fieldErrorId(paymentModelGroupId);
  const categoryGroupId = "create-activity-category";
  const categoryErrorId = fieldErrorId(categoryGroupId);

  const addressLabels = useMemo(
    () => ({
      search: tCommon("addressSearch.label"),
      searchPlaceholder: tCommon("addressSearch.placeholder"),
    }),
    [tCommon],
  );

  const locationSyncKey = isOpen ? `modal-${adminStep}` : "closed";

  const { requestInvalidAddressSaveConfirm, confirmModal } =
    useInvalidAddressSaveConfirm();

  const handleDescriptionTranslationsChange = (
    values: TranslationFormValues,
  ) => {
    setDescriptionTranslations(values);
    if (descriptionError) {
      setDescriptionError(undefined);
    }
  };

  const handleNameTranslationsChange = (values: TranslationFormValues) => {
    setNameTranslations(values);
    if (nameError) {
      setNameError(undefined);
    }
  };

  const preferredSourceLanguage = resolveLocale(i18n.language);
  const autoTranslateSourceLanguage =
    findAutoTranslateSourceLanguage(nameTranslations, preferredSourceLanguage) ??
    preferredSourceLanguage;

  const autoTranslate = useCatalogAutoTranslate({
    sourceLanguage: autoTranslateSourceLanguage,
    nameTranslations,
    descriptionTranslations,
    requireDescription: false,
    onApplied: (result) => {
      const applied = applyAutoTranslateResult(
        nameTranslations,
        descriptionTranslations,
        result,
        { mergeDescriptions: true },
      );
      setNameTranslations(applied.nameTranslations);
      setDescriptionTranslations(applied.descriptionTranslations);
      setNameError(undefined);
      setDescriptionError(undefined);
    },
    onMissingName: () => setNameError(t("zod.nameRequired")),
  });

  const submitNewActivity = async (data: ActivityFormData) => {
    if (isLoading) {
      return;
    }
    const resolvedOwnerId = fixedOwnerUserId ?? ownerUserId;
    if (!resolvedOwnerId) {
      return;
    }
    if (!regionId) {
      setRegionError(t("createModal.regionRequired"));
      setAdminStep(1);
      return;
    }
    setRegionError(undefined);
    if (data.category === undefined || data.payment_model === undefined) {
      return;
    }

    const names = nameTranslations;
    const descriptions = descriptionTranslations;

    if (findMissingTranslations(names).length > 0) {
      setNameError(t("zod.allTranslationsRequired"));
      setAdminStep(1);
      return;
    }
    if (hasPartialTranslations(descriptions)) {
      setDescriptionError(t("zod.descriptionRequired"));
      setAdminStep(1);
      return;
    }
    if (findTranslationOverMaxLength(names, ACTIVITY_NAME_MAX_LENGTH)) {
      setNameError(tValidation("maxChars", { max: ACTIVITY_NAME_MAX_LENGTH }));
      setAdminStep(1);
      return;
    }
    if (
      findTranslationOverMaxLength(
        descriptions,
        ACTIVITY_DESCRIPTION_MAX_LENGTH,
      )
    ) {
      setDescriptionError(
        tValidation("maxChars", { max: ACTIVITY_DESCRIPTION_MAX_LENGTH }),
      );
      setAdminStep(1);
      return;
    }

    const flowForPaymentModel = (paymentModel: ActivityPaymentModel) => {
      if (paymentModel === ActivityPaymentModel.RECEIPT_BASED) {
        return {
          booking_quantity_mode: BookingQuantityMode.GUESTS,
          availability_mode: ActivityAvailabilityMode.SCHEDULED,
          requires_handshake: true,
        };
      }
      return {
        booking_quantity_mode: BookingQuantityMode.QUANTITY,
        availability_mode: ActivityAvailabilityMode.SCHEDULED,
        requires_handshake: false,
      };
    };

    const activityData: ActivityCreate = {
      category: data.category,
      payment_model: data.payment_model,
      ...flowForPaymentModel(data.payment_model),
      name_translations: buildRequiredTranslations(names),
      region_id: regionId,
      description_translations: buildItemDescriptionTranslations(descriptions),
      ...(noFixedLocation
        ? {
            address: "",
            city: "",
            postal_code: "",
            country: "",
            coordinates: null,
          }
        : {
            address: location.address,
            city: "",
            postal_code: "",
            country: "",
            coordinates: location.coordinates,
          }),
      commission_rate: rateFromUIPercent(data.commission_percent),
      owner_user_id: resolvedOwnerId,
    };
    if (salesAgentUserId) {
      const saRate = rateFromPercentInput(salesAgentCommissionPct.trim());
      if (saRate === null) {
        toast.error(t("createModal.salesAgentCutRequired"));
        setAdminStep(2);
        return;
      }
      activityData.sales_agent_user_id = salesAgentUserId;
      activityData.sales_agent_commission_rate = saRate;
    }
    onSubmit(activityData);
  };

  const handleFormSubmit = (data: ActivityFormData) => {
    const hasPhysicalAddress = !noFixedLocation;
    requestInvalidAddressSaveConfirm(
      hasPhysicalAddress,
      addressValidation,
      tCommon("addressValidation.saveWarningActivity"),
      () => submitNewActivity(data),
    );
  };

  const handleClose = () => {
    reset(DEFAULT_FORM);
    setOwnerSearch("");
    setOwnerUserId("");
    setRegionId("");
    setRegionError(undefined);
    setSalesAgentUserId("");
    setSalesAgentSearch("");
    setSalesAgentCommissionPct("");
    setSalesAgentCommissionError(undefined);
    setAdminStep(1);
    setNoFixedLocation(false);
    setLocation(emptyGeocodedLocation());
    setAddressValidation({ status: "idle" });
    setDescriptionTranslations({});
    setDescriptionError(undefined);
    setNameTranslations({});
    setNameError(undefined);
    onClose();
  };

  const handleAdminStep1Next = async () => {
    const ok = await trigger([
      "commission_percent",
      "category",
      "payment_model",
    ]);
    if (!ok) return;
    if (findMissingTranslations(nameTranslations).length > 0) {
      const sourceLanguage = findAutoTranslateSourceLanguage(
        nameTranslations,
        preferredSourceLanguage,
      );
      if (!sourceLanguage) {
        setNameError(t("zod.nameRequired"));
        return;
      }
      setNameError(t("zod.allTranslationsRequired"));
      return;
    }
    if (hasPartialTranslations(descriptionTranslations)) {
      setDescriptionError(t("zod.descriptionRequired"));
      return;
    }
    if (!fixedOwnerUserId && (!ownerUserId || ownerSelectOptions.length === 0)) {
      return;
    }
    if (!regionId) {
      setRegionError(t("createModal.regionRequired"));
      return;
    }
    setRegionError(undefined);
    setAdminStep(2);
  };

  const handleSkipSalesAgent = () => {
    setSalesAgentUserId("");
    setSalesAgentSearch("");
    setSalesAgentCommissionPct("");
    setSalesAgentCommissionError(undefined);
    setAdminStep(3);
  };

  const handleAdminStep2Next = () => {
    setSalesAgentCommissionError(undefined);
    if (salesAgentUserId) {
      const saRate = rateFromPercentInput(salesAgentCommissionPct.trim());
      if (saRate === null) {
        setSalesAgentCommissionError(t("createModal.salesAgentCutRequired"));
        return;
      }
    }
    setAdminStep(3);
  };

  const toggleNoFixedLocation = () => {
    if (!noFixedLocation) {
      locationDraftBackup.current = location;
      setLocation(emptyGeocodedLocation());
      setNoFixedLocation(true);
      setAddressValidation({ status: "idle" });
      return;
    }
    setLocation(locationDraftBackup.current);
    setNoFixedLocation(false);
  };

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen}
      onClose={handleClose}
      title={t("createModal.title")}
      size="xl"
      footer={
        <ModalFooter>
          {adminStep === 1 && (
            <>
              <Button type="button" variant="ghost" onClick={handleClose}>
                {tCommon("cancel")}
              </Button>
              <Button type="button" onClick={handleAdminStep1Next}>
                {t("createModal.wizardNext")}
              </Button>
            </>
          )}
          {adminStep === 2 && (
            <>
              <Button type="button" variant="ghost" onClick={() => setAdminStep(1)}>
                {tCommon("back")}
              </Button>
              <Button type="button" variant="outline" onClick={handleSkipSalesAgent}>
                {t("createModal.wizardSkip")}
              </Button>
              <Button type="button" onClick={handleAdminStep2Next}>
                {t("createModal.wizardNext")}
              </Button>
            </>
          )}
          {adminStep === 3 && (
            <>
              <Button type="button" variant="ghost" onClick={() => setAdminStep(2)}>
                {tCommon("back")}
              </Button>
              <Button type="button" variant="ghost" onClick={handleClose}>
                {tCommon("cancel")}
              </Button>
              <Button
                type="submit"
                form={formId}
                isLoading={isLoading}
                disabled={
                  !regionId ||
                  (!fixedOwnerUserId &&
                    (!ownerUserId || ownerSelectOptions.length === 0))
                }
              >
                {t("createModal.createActivity")}
              </Button>
            </>
          )}
        </ModalFooter>
      }
    >
      <form
        id={formId}
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-5"
      >
        <WizardStepper
          current={adminStep}
          counterLabel={t("createModal.wizardStepCounter", {
            current: adminStep,
            total: 3,
          })}
          steps={[
            t("createModal.wizardStepDetails"),
            t("createModal.wizardStepSalesAgent"),
            t("createModal.location"),
          ]}
        />
        {adminStep === 1 && (
          <div className="space-y-3">
            <RegionSelect
              value={regionId}
              onChange={(value) => {
                setRegionId(value);
                if (regionError) setRegionError(undefined);
              }}
              label={t("createModal.regionLabel")}
              placeholder={t("createModal.chooseRegion")}
              error={regionError}
              required
            />
            {!fixedOwnerUserId && (
              <>
                <Label as="p" emphasis="semibold" className="text-surface-800">
                  {t("createModal.owner")}
                </Label>
                <Input
                  label={t("createModal.searchOwnerLabel")}
                  placeholder={t("createModal.searchOwnerPlaceholder")}
                  value={ownerSearch}
                  onChange={(e) => setOwnerSearch(e.target.value)}
                  autoComplete="off"
                />
                <Select
                  label={t("createModal.selectAccount")}
                  placeholder={
                    ownersLoading
                      ? tCommon("loading")
                      : t("createModal.chooseOwner")
                  }
                  options={ownerSelectOptions}
                  value={ownerUserId}
                  onChange={(e) => setOwnerUserId(e.target.value)}
                  disabled={ownersLoading}
                  error={
                    ownersLoaded && ownerSelectOptions.length === 0
                      ? t("createModal.noOwnersError")
                      : undefined
                  }
                />
                {ownersLoadError ? (
                  <QueryLoadError failure={ownersLoadError} />
                ) : null}
              </>
            )}
            <CatalogTranslatePanel
              hint={t("detailForm.autoTranslateHint")}
              buttonLabel={t("detailForm.autoTranslateGenerate")}
              onTranslate={autoTranslate.run}
              isLoading={autoTranslate.isPending}
              disabled={isLoading || autoTranslate.isPending}
            />
            <CatalogTranslatedField
              values={nameTranslations}
              onChange={handleNameTranslationsChange}
              label={t("createModal.activityName")}
              fieldAriaLabel={(language) =>
                t("detailForm.nameLanguageCardAria", { language })
              }
              error={nameError}
              required
              maxLength={ACTIVITY_NAME_MAX_LENGTH}
              selectedLanguage={copyLanguage}
              onSelectedLanguageChange={setCopyLanguage}
            />
            <CatalogTranslatedField
              values={descriptionTranslations}
              onChange={handleDescriptionTranslationsChange}
              label={t("createModal.description")}
              fieldAriaLabel={(language) =>
                t("detailForm.descriptionLanguageCardAria", { language })
              }
              multiline
              placeholder={t("createModal.descriptionPlaceholder")}
              maxLength={ACTIVITY_DESCRIPTION_MAX_LENGTH}
              error={descriptionError}
              hint={t("detailForm.descriptionHint")}
              selectedLanguage={copyLanguage}
              onSelectedLanguageChange={setCopyLanguage}
              hideLanguageControls
            />
            <PercentInput
              {...register("commission_percent", { valueAsNumber: true })}
              label={t("createModal.commissionPercentLabel")}
              required
              error={errors.commission_percent?.message}
            />

            <fieldset
              id={paymentModelGroupId}
              className="border-0 p-0"
              aria-labelledby={`${paymentModelGroupId}-label`}
              aria-invalid={errors.payment_model ? "true" : undefined}
              aria-describedby={
                errors.payment_model ? paymentModelErrorId : undefined
              }
            >
              <Label
                as="p"
                id={`${paymentModelGroupId}-label`}
                spacing="relaxed"
                emphasis="semibold"
                className="text-surface-800"
              >
                {t("createModal.paymentModel")}
                <span className="text-error-500"> *</span>
              </Label>
              <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
                {paymentModelCards.map(({ value, label, description, Icon }) => {
                  const selected = paymentModelValue === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setValue("payment_model", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      className={cn(
                        "flex h-full min-h-0 w-full items-start gap-3 rounded-lg border p-3 text-left transition-[border-color,background-color,box-shadow] duration-150",
                        selected
                          ? "border-primary-500 bg-primary-50/60 ring-[3px] ring-primary-500/15"
                          : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:bg-surface-50",
                      )}
                      aria-pressed={selected}
                    >
                      <span
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md border",
                          selected
                            ? "border-primary-200 bg-primary-100 text-primary-700"
                            : "border-surface-200 bg-surface-50 text-surface-500",
                        )}
                      >
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-surface-900">
                          {label}
                        </span>
                        <span className="mt-0.5 block text-[13px] text-surface-500">
                          {description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.payment_model ? (
                <FieldError id={paymentModelErrorId}>
                  {errors.payment_model.message}
                </FieldError>
              ) : null}
            </fieldset>

            <fieldset
              id={categoryGroupId}
              className="border-0 p-0"
              aria-labelledby={`${categoryGroupId}-label`}
              aria-invalid={errors.category ? "true" : undefined}
              aria-describedby={
                errors.category ? categoryErrorId : undefined
              }
            >
              <Label
                as="p"
                id={`${categoryGroupId}-label`}
                spacing="relaxed"
                emphasis="semibold"
                className="text-surface-800"
              >
                {t("createModal.catalogCategory")}
                <span className="text-error-500"> *</span>
              </Label>
              <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-3">
                {ACTIVITY_TYPE_ORDER.map((cat) => {
                  const cfg = ACTIVITY_TYPE_CONFIG[cat];
                  const Icon = cfg.icon;
                  const selected = categoryValue === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        setValue("category", cat, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      className={cn(
                        "flex h-full min-h-0 w-full items-start gap-3 rounded-lg border p-3 text-left transition-[border-color,background-color,box-shadow] duration-150",
                        selected
                          ? "border-primary-500 bg-primary-50/60 ring-[3px] ring-primary-500/15"
                          : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:bg-surface-50",
                      )}
                      aria-pressed={selected}
                    >
                      <span
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md border",
                          selected
                            ? "border-primary-200 bg-primary-100 text-primary-700"
                            : "border-surface-200 bg-surface-50 text-surface-500",
                        )}
                      >
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="self-center text-sm font-medium text-surface-900">
                        {activityTypeLabel(cat)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.category ? (
                <FieldError id={categoryErrorId}>
                  {errors.category.message}
                </FieldError>
              ) : null}
            </fieldset>
          </div>
        )}

        {adminStep === 2 && (
          <div className="space-y-3">
            <p className="text-[13px] text-surface-500">
              {t("createModal.wizardStep2Lead")}
            </p>
            <Label as="p" emphasis="semibold" className="text-surface-800">
              {t("createModal.salesAgent")}
            </Label>
            <PickerPanel
              searchPlaceholder={t("createModal.salesAgentSearchPlaceholder")}
              searchValue={salesAgentSearch}
              onSearchChange={setSalesAgentSearch}
              isLoading={salesAgentsQuery.isLoading}
              loadError={queryLoadFailure(
                salesAgentsQuery,
                tErrors("loadSalesAgents"),
              )}
              emptyMessage={
                salesAgentSearch.trim()
                  ? t("createModal.salesAgentNoResults")
                  : t("createModal.salesAgentListEmpty")
              }
              items={salesAgentsDisplayed.map((sa) => ({
                id: sa.user_id,
                title: sa.email,
                selected: salesAgentUserId === sa.user_id,
                onSelect: () => {
                  setSalesAgentUserId(sa.user_id);
                  setSalesAgentSearch(sa.email);
                  setSalesAgentCommissionPct("");
                  setSalesAgentCommissionError(undefined);
                },
              }))}
            />
            <PercentInput
              id="create-activity-sales-agent-cut"
              label={t("createModal.salesAgentCutPercentLabel")}
              value={salesAgentCommissionPct}
              onChange={(e) => {
                setSalesAgentCommissionPct(e.target.value);
                if (salesAgentCommissionError) {
                  setSalesAgentCommissionError(undefined);
                }
              }}
              required={!!salesAgentUserId}
              error={salesAgentCommissionError}
            />
          </div>
        )}

        {adminStep === 3 && (
          <div className="space-y-4">
            <p className="text-[13px] text-surface-500">
              {t("createModal.wizardStep3Lead")}
            </p>
            <div className="space-y-4 rounded-lg border border-surface-200 bg-surface-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <h4 className="text-sm font-semibold text-surface-900">
                  {t("createModal.location")}
                </h4>
                <Button
                  type="button"
                  variant={noFixedLocation ? "primary" : "outline"}
                  size="sm"
                  className="shrink-0 self-start sm:self-auto"
                  onClick={toggleNoFixedLocation}
                  aria-pressed={noFixedLocation}
                >
                  {t("createModal.withoutLocation")}
                </Button>
              </div>
              {noFixedLocation ? (
                <p className="text-sm text-surface-700">
                  {t("createModal.noLocationSelectedMessage")}
                </p>
              ) : (
                <EditableGeocodedLocation
                  value={location}
                  onChange={setLocation}
                  labels={addressLabels}
                  syncKey={locationSyncKey}
                  onValidationChange={setAddressValidation}
                  layout="stack"
                />
              )}
            </div>
          </div>
        )}

      </form>
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        confirmText={tCommon("confirm")}
        isOpen={autoTranslate.confirmOpen}
        onClose={autoTranslate.onConfirmClose}
        onConfirm={autoTranslate.onConfirm}
        title={autoTranslate.confirmTitle}
        message={autoTranslate.confirmMessage}
        isLoading={autoTranslate.isPending}
      />
      {confirmModal}
    </Modal>
  );
}
